if (!customElements.get('cd-community')) {
  class CloudDreamCommunity extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cd-community-dialog]');
      this.joinPanel = this.querySelector('[data-cd-community-join]');
      this.sticky = this.querySelector('[data-cd-community-sticky]');
      this.openers = Array.from(this.querySelectorAll('[data-cd-community-open]'));
      this.lastOpener = null;

      this.onOpen = this.onOpen.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onJoinPointerEnter = this.onJoinPointerEnter.bind(this);

      this.openers.forEach((opener) => opener.addEventListener('click', this.onOpen));
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('close', this.onDialogClose);
      this.joinPanel?.addEventListener('pointerenter', this.onJoinPointerEnter);

      this.observeRive();
      this.observeJoinPanel();
    }

    disconnectedCallback() {
      this.openers?.forEach((opener) => opener.removeEventListener('click', this.onOpen));
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.joinPanel?.removeEventListener('pointerenter', this.onJoinPointerEnter);
      this.joinObserver?.disconnect();
      this.riveLoadObserver?.disconnect();
      this.riveResizeObserver?.disconnect();
      this.riveInstance?.cleanup();
    }

    onOpen(event) {
      if (!this.dialog) return;
      this.lastOpener = event.currentTarget;
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.dialog.close();
    }

    onDialogClose() {
      this.lastOpener?.focus({ preventScroll: true });
    }

    onJoinPointerEnter() {
      this.riveNudgeTrigger?.fire();
    }

    observeRive() {
      const canvas = this.querySelector('[data-cd-community-rive]');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (!canvas?.dataset.riv || reduceMotion || isMobile) return;

      if (!('IntersectionObserver' in window)) {
        this.loadRive(canvas);
        return;
      }

      this.riveLoadObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this.riveLoadObserver.disconnect();
          this.loadRive(canvas);
        },
        { rootMargin: '600px 0px' }
      );
      this.riveLoadObserver.observe(canvas);
    }

    loadRive(canvas) {
      if (window.rive) {
        this.startRive(canvas);
        return;
      }

      if (!canvas.dataset.runtime) return;
      window.cdCommunityRiveRuntime ||= new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = canvas.dataset.runtime;
        script.async = true;
        script.dataset.cdCommunityRiveRuntime = '';
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', reject, { once: true });
        document.head.append(script);
      });

      window.cdCommunityRiveRuntime
        .then(() => {
          if (this.isConnected) this.startRive(canvas);
        })
        .catch(() => canvas.classList.add('is-unavailable'));
    }

    startRive(canvas) {
      if (this.riveInstance || !window.rive) return;

      if (canvas.dataset.wasm) window.rive.RuntimeLoader.setWasmUrl(canvas.dataset.wasm);

      const layoutFor = () =>
        canvas.clientWidth < 750
          ? new window.rive.Layout({ fit: window.rive.Fit.Contain, alignment: window.rive.Alignment.Center })
          : new window.rive.Layout({ fit: window.rive.Fit.Cover, alignment: window.rive.Alignment.Center });

      try {
        const options = {
          src: canvas.dataset.riv,
          canvas,
          autoplay: true,
          layout: layoutFor(),
          onLoad: () => {
            this.riveInstance.resizeDrawingSurfaceToCanvas();
            canvas.classList.add('is-ready');
            this.bindRiveTriggers(canvas.dataset.machine);
          },
        };
        if (canvas.dataset.artboard) options.artboard = canvas.dataset.artboard;
        if (canvas.dataset.machine) options.stateMachines = canvas.dataset.machine;
        this.riveInstance = new window.rive.Rive(options);
        this.riveWasMobile = canvas.clientWidth < 750;

        if ('ResizeObserver' in window) {
          this.riveResizeObserver = new ResizeObserver(() => {
            const isMobile = canvas.clientWidth < 750;
            if (isMobile !== this.riveWasMobile) {
              this.riveInstance.layout = layoutFor();
              this.riveWasMobile = isMobile;
            }
            this.riveInstance?.resizeDrawingSurfaceToCanvas();
          });
          this.riveResizeObserver.observe(canvas);
        }
      } catch (error) {
        canvas.classList.add('is-unavailable');
      }
    }

    bindRiveTriggers(stateMachineName) {
      if (!stateMachineName || !this.riveInstance) return;
      const inputs = this.riveInstance.stateMachineInputs(stateMachineName) || [];
      this.riveNudgeTrigger = inputs.find((input) => input.name === 'Trigger 2');
    }

    observeJoinPanel() {
      if (!this.sticky) return;

      this.sticky.hidden = false;
      if (!this.joinPanel || !('IntersectionObserver' in window)) {
        this.sticky.classList.add('is-visible');
        return;
      }

      this.joinObserver = new IntersectionObserver(
        ([entry]) => {
          const joinIsBelowViewport = !entry.isIntersecting && entry.boundingClientRect.top > 0;
          this.sticky.classList.toggle('is-visible', joinIsBelowViewport);
        },
        { threshold: 0 }
      );
      this.joinObserver.observe(this.joinPanel);
    }
  }

  customElements.define('cd-community', CloudDreamCommunity);
}
